import { useCallback, useMemo } from 'react';

import { GroupedOption, SelectOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { VolumesAutoCompleteResponse } from '~/shared/types/api';

import {
  buildFilterSearchAriaLabel,
  volumeFilterSearchInputId,
} from '~/shared/components/searchInputMeta';

import { useSearchFilter } from './common/useSearchFilter';
import SearchFilterBase from './common/SearchFilterBase';
import SearchFilterOptionRow from './common/SearchFilterOptionRow';
import {
  buildTagPairAutocompleteQuery,
  formatTagPairDisplay,
  parseTagPair,
  tagPairIdentityFromKeyValue,
  tagPairIdentityFromOption,
  tagPairIdentityFromPair,
} from './tagPair';

export default function SearchVolumeTags({
  filters,
  setFilters,
  isDisclosureOpen,
  accounts,
}: {
  filters: SelectOption[];
  setFilters: (selections: SelectOption[]) => void;
  isDisclosureOpen?: boolean;
  accounts?: string;
}) {
  const api = useXFetch();

  const selectedIdByTagIdentity = useMemo(
    () =>
      new Map<string, string>(
        filters
          .filter((f) => f.group.startsWith('tag:'))
          .map((f) => [tagPairIdentityFromPair(f.id), f.id])
      ),
    [filters]
  );

  const searchOptions = useCallback(
    async (pattern: string): Promise<GroupedOption[]> => {
      const qs = buildTagPairAutocompleteQuery(pattern, accounts);
      if (!qs) return [];

      try {
        const response = await api<VolumesAutoCompleteResponse>(
          `/api/v1/volumes/autocomplete?${qs}`,
          { cache: 'no-store' }
        );
        const values = response.values || [];

        const tagOptions = values.reduce<SelectOption[]>((acc, rawValue) => {
          const pair = String(rawValue || '').trim();
          if (!pair) {
            return acc;
          }

          const { key, value } = parseTagPair(pair);
          if (!key) {
            return acc;
          }

          const normalizedIdentity = tagPairIdentityFromKeyValue(key, value);
          const existingId = selectedIdByTagIdentity.get(normalizedIdentity);
          const displayId = existingId ?? formatTagPairDisplay(key, value);

          acc.push({
            id: displayId,
            name: pair,
            group: `tag:${key}` as SelectOption['group'],
          });

          return acc;
        }, []);

        if (tagOptions.length === 0) {
          return [];
        }

        return [{ label: 'Tags', options: tagOptions }];
      } catch (error) {
        console.error('[VolumeTagsFilter] Error searching tags:', error);
        return [];
      }
    },
    [api, accounts, selectedIdByTagIdentity]
  );

  const {
    searchResults,
    isLoading,
    inputValue,
    showResults,
    focusedIndex,
    localSelections,
    isOptionSelected,
    flattenedResults,
    inputRef,
    resultsRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSelect,
    handleResultsMouseDown,
    handleResultsMouseUp,
    handleKeyDown,
  } = useSearchFilter({
    initialSelections: filters,
    onSelectionsChange: setFilters,
    searchFunction: searchOptions,
    filterGroup: 'tags',
    debounceMs: 200,
    isDisclosureOpen,
    optionSelectionKey: tagPairIdentityFromOption,
  });

  const renderOption = useCallback(
    (option: SelectOption, isSelected: boolean, isFocused: boolean = false) => (
      <SearchFilterOptionRow
        option={option}
        isSelected={isSelected}
        onSelect={handleSelect}
        tagVariant="volume"
        isFocused={isFocused}
      />
    ),
    [handleSelect]
  );

  return (
    <div className="flex w-full flex-1 flex-col">
      <SearchFilterBase
        inputValue={inputValue}
        onInputChange={handleInputChange}
        inputRef={inputRef}
        isDisclosureOpen={isDisclosureOpen}
        searchInputId={volumeFilterSearchInputId('volume-tags')}
        searchAriaLabel={buildFilterSearchAriaLabel('Volume Tags')}
        placeholder="Search (key: value)"
        searchResults={searchResults}
        isLoading={isLoading}
        showResults={showResults}
        focusedIndex={focusedIndex}
        flattenedResults={flattenedResults}
        localSelections={localSelections}
        onSelect={handleSelect}
        isOptionSelected={isOptionSelected}
        renderOption={renderOption}
        className="w-full"
        mustTypeFirstMessage="Search (key: value)"
        resultsRef={resultsRef}
        onResultsMouseDown={handleResultsMouseDown}
        onResultsMouseUp={handleResultsMouseUp}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
      />
    </div>
  );
}
